import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../material.module';
import { ApplicationDetail, RejectionReason } from '../../dashboard/dashboard.models';
import { UserSearchResult, AssignmentService, ModernizationPod } from '../../applications/Assignment/assignment.service';
import { PatUpdateDialogComponent } from '../pat-update-dialog/pat-update-dialog.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError, map, tap } from 'rxjs/operators';

export type ActionType = 'approve' | 'override' | 'assign-technical' | 'reject' | 'assign-amore' | 'assign-pod';

export interface ActionDialogData {
    application: ApplicationDetail;
    rejectionReasons: RejectionReason[];
    isAMORETeam: boolean;
    currentRecommendation: string;
    currentUserEmail?: string;
    initialStep?: 1 | 2 | 3;
    initialSelectedAction?: ActionType;
    initialAmoreAssignType?: 'myself' | 'architect' | 'pod';
    initialAssignEmail?: string;
    initialAssignNotes?: string;
    initialSelectedPodId?: number | null;
    initialConsentProvided?: boolean;
}

export interface ActionDialogResult {
    action: ActionType;
    // Assign fields
    assignToEmail?: string;
    assignToName?: string;
    notes?: string;
    // Rejection fields
    rejectionReasonId?: number;
    rejectionReason?: string;
    otherReasonText?: string;
    // Override/Approve fields
    overrideAction?: string;
    overrideReason?: string;
    // POD assignment fields
    selectedPodId?: number | null;
    selectedPodName?: string;
}

interface ActionOption {
    id: ActionType;
    title: string;
    description: string;
    icon: string;
}

@Component({
    selector: 'app-action-dialog',
    templateUrl: './action-dialog.component.html',
    styleUrls: ['./action-dialog.component.css'],
    imports: [CommonModule, FormsModule, ReactiveFormsModule, MaterialModule]
})
export class ActionDialogComponent implements OnInit {
    step: 1 | 2 | 3 = 1;
    // AMORE-specific assignment type for step 2
    amoreAssignType: 'myself' | 'architect' | 'pod' = 'myself';
    currentUserEmail: string = '';

    private readonly allActionOptions: ActionOption[] = [
        {
            id: 'approve',
            title: 'Approve Assessment',
            description: 'Approve the AI disposition for this application.',
            icon: 'check_circle'
        },
        {
            id: 'override',
            title: 'Override Assessment',
            description: 'Override the AI disposition for this application.',
            icon: 'gavel'
        },
        {
            id: 'reject',
            title: 'Reject Assessment',
            description: 'Reject this assessment with a reason.',
            icon: 'cancel'
        },
        {
            id: 'assign-amore',
            title: 'Assign to AMORE Team',
            description: 'Assign this application to the AMORE (ARM) team for implementation.',
            icon: 'groups'
        },
        {
            id: 'assign-technical',
            title: 'Assign to Technical Team',
            description: 'Assign this application to a technical team member for review.',
            icon: 'person'
        }
    ];

    get actionOptions(): ActionOption[] {
        if (this.data.isAMORETeam) {
            return this.allActionOptions
                .filter(o => o.id !== 'assign-technical' && o.id !== 'assign-pod')
                .map(o => o.id === 'assign-amore'
                    ? {
                        ...o,
                        title: 'Assign Application',
                        description: 'Assign this application to yourself, an architect, or a POD.'
                    }
                    : o
                );
        }
        return this.allActionOptions.filter(o => o.id !== 'assign-pod');
    }

    selectedAction: ActionType | null = null;
    validationErrors: string[] = [];

    // Assign Technical / AMORE fields
    assignEmail: string = '';
    assignNotes: string = '';
    consentProvided: boolean = false;

    // POD assignment fields
    pods: ModernizationPod[] = [];
    selectedPodId: number | null = null;
    emailControl = new FormControl('');
    filteredUsers$!: Observable<UserSearchResult[]>;
    showSuggestions: boolean = false;
    emailSelectedFromSearch: boolean = false;

    // Rejection fields
    rejectionReasonId: number | null = null;
    rejectionReason: string = '';
    otherReasonText: string = '';

    // Override/Approve fields
    overrideAction: string = '';
    overrideReason: string = '';

    readonly allModernizationOptions = [
        { value: 'Modernize', label: 'Modernize' },
        { value: 'Already Modern', label: 'Already Modern' },
        { value: 'Decommission', label: 'Decommission' },
        { value: 'Excluded', label: 'Excluded' }
    ];

    get modernizationOptions() {
        return this.allModernizationOptions.filter(o =>
            o.value !== this.data.currentRecommendation
        );
    }

    constructor(
        public dialogRef: MatDialogRef<ActionDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ActionDialogData,
        private assignmentService: AssignmentService,
        private dialog: MatDialog
    ) { }

    openPatUpdateDialog(): void {
        this.dialog.open(PatUpdateDialogComponent, {
            width: '700px',
            disableClose: true,
            data: {
                title: 'Update Azure DevOps PAT',
                intro: 'Approving modernization creates or updates an Azure DevOps work item. Update your PAT if needed before submitting.'
            }
        });
    }

    ngOnInit(): void {
        if (this.data.isAMORETeam) {
            this.assignmentService.getVisiblePods().subscribe({
                next: (pods) => { this.pods = pods; },
                error: () => { this.pods = []; }
            });
            this.currentUserEmail = this.data.currentUserEmail || '';
            this.amoreAssignType = 'myself';
        }

        this.applyInitialState();

        this.filteredUsers$ = this.emailControl.valueChanges.pipe(
            tap(() => { this.emailSelectedFromSearch = false; }),
            debounceTime(300),
            distinctUntilChanged(),
            switchMap(value => {
                const searchTerm = typeof value === 'string' ? value : '';
                this.assignEmail = searchTerm;
                if (searchTerm && searchTerm.length >= 3) {
                    return this.assignmentService.searchUsers(searchTerm).pipe(
                        map(response => response.value || []),
                        catchError(() => of([]))
                    );
                }
                return of([]);
            })
        );
    }

    private applyInitialState(): void {
        if (this.data.initialSelectedAction) {
            this.selectedAction = this.data.initialSelectedAction;
        }

        if (this.data.initialAmoreAssignType) {
            this.amoreAssignType = this.data.initialAmoreAssignType;
        }

        if (this.data.initialAssignEmail) {
            this.assignEmail = this.data.initialAssignEmail;
            this.emailControl.setValue(this.data.initialAssignEmail, { emitEvent: false });
            this.emailSelectedFromSearch = true;
        }

        if (this.data.initialAssignNotes) {
            this.assignNotes = this.data.initialAssignNotes;
        }

        if (this.data.initialSelectedPodId !== undefined) {
            this.selectedPodId = this.data.initialSelectedPodId;
        }

        if (this.data.initialConsentProvided !== undefined) {
            this.consentProvided = this.data.initialConsentProvided;
        }

        if (this.data.initialStep) {
            this.step = this.data.initialStep;
        }
    }

    get selectedActionOption(): ActionOption | undefined {
        return this.actionOptions.find(a => a.id === this.selectedAction);
    }

    selectAction(action: ActionType): void {
        this.selectedAction = action;
    }

    onNext(): void {
        this.validationErrors = [];

        if (this.step === 1) {
            if (!this.selectedAction) {
                this.validationErrors = ['Please select an action.'];
                return;
            }
            if (this.selectedAction == 'assign-technical') {
                this.emailControl.setValue(this.data.application.technicalOwnerEmail);
                this.assignEmail = this.emailControl.value || '';
                this.emailSelectedFromSearch = true;
            }
            if (this.selectedAction === 'approve') {
                this.step = 3;
            }
            else {
                this.step = 2;
            }
        } else if (this.step === 2) {
            if (this.data.isAMORETeam && this.selectedAction === 'assign-amore') {
                if (!this.validateAmoreDetails()) return;
                this.step = 3;
            } else {
                if (!this.validateDetails()) return;
                this.step = 3;
            }
        }
    }
    // Validation for AMORE step 2
    private validateAmoreDetails(): boolean {
        const errors: string[] = [];
        if (!this.amoreAssignType) {
            errors.push('Please select an assignment type.');
        }
        if (this.amoreAssignType === 'myself') {
            if (!this.currentUserEmail) {
                errors.push('Current user email not found.');
            }
        }
        if (this.amoreAssignType === 'architect') {
            const email = this.assignEmail?.trim() || '';
            if (!email) {
                errors.push('Please select an assignee.');
            } else if (!this.isValidEmail(email)) {
                errors.push('Please enter a valid email address.');
            } else if (!this.emailSelectedFromSearch) {
                errors.push('Please select a user from the search results.');
            }
        }
        if (this.amoreAssignType === 'pod') {
            if (!this.selectedPodId) {
                errors.push('Please select a POD.');
            }
        }
        if (!this.consentProvided) {
            errors.push('Please provide your consent to proceed.');
        }
        this.validationErrors = errors;
        return errors.length === 0;
    }

    onBack(): void {
        this.validationErrors = [];
        if (this.step === 3) {
            this.step = 2;
        } else if (this.step === 2) {
            this.consentProvided = false;
            this.step = 1;
        }
    }

    private validateDetails(): boolean {
        const errors: string[] = [];

        if (this.selectedAction === 'override') {
            if (!this.overrideAction) {
                errors.push('Please select a final decision.');
            }
            if (!this.overrideReason?.trim()) {
                errors.push('Please provide an override reason.');
            }
        }

        if (this.selectedAction === 'assign-technical') {
            const email = this.assignEmail?.trim() || '';
            if (!email) {
                errors.push('Please select an assignee.');
            } else if (!this.isValidEmail(email)) {
                errors.push('Please enter a valid email address.');
            } else if (!this.emailSelectedFromSearch) {
                errors.push('Please select a user from the search results.');
            }
        }

        if (this.selectedAction === 'assign-pod') {
            if (!this.selectedPodId) {
                errors.push('Please select a POD.');
            }
        }

        if (this.selectedAction === 'assign-technical' || this.selectedAction === 'assign-amore' || this.selectedAction === 'assign-pod') {
            if (!this.consentProvided) {
                errors.push('Please provide your consent to proceed.');
            }
        }

        if (this.selectedAction === 'reject') {
            if (!this.rejectionReasonId) {
                errors.push('Please select a rejection reason.');
            }
            const selectedReason = this.data.rejectionReasons.find(r => r.rejectionReasonId === this.rejectionReasonId);
            if (selectedReason?.rejectionReason?.includes('Other') && !this.otherReasonText?.trim()) {
                errors.push('Please provide a detailed reason for rejection.');
            }
        }

        this.validationErrors = errors;
        return errors.length === 0;
    }

    onSubmit(): void {
        const result: ActionDialogResult = {
            action: this.selectedAction!
        };

        if (this.data.isAMORETeam && this.selectedAction === 'assign-amore') {
            if (this.amoreAssignType === 'myself') {
                result.assignToEmail = this.currentUserEmail;
                result.notes = this.assignNotes;
            } else if (this.amoreAssignType === 'architect') {
                result.assignToEmail = this.assignEmail;
                result.notes = this.assignNotes;
            } else if (this.amoreAssignType === 'pod') {
                result.selectedPodId = this.selectedPodId;
                result.selectedPodName = this.pods.find(p => p.podId === this.selectedPodId)?.name || '';
                result.notes = this.assignNotes;
            }
        } else {
            if (this.selectedAction === 'assign-technical') {
                result.assignToEmail = this.assignEmail;
                result.notes = this.assignNotes;
            }
            if (this.selectedAction === 'override') {
                result.overrideAction = this.overrideAction;
                result.overrideReason = this.overrideReason;
            }
            if (this.selectedAction === 'assign-amore') {
                result.notes = this.assignNotes;
            }
            if (this.selectedAction === 'assign-pod') {
                result.selectedPodId = this.selectedPodId;
                result.selectedPodName = this.pods.find(p => p.podId === this.selectedPodId)?.name || '';
                result.notes = this.assignNotes;
            }
            if (this.selectedAction === 'reject') {
                result.rejectionReasonId = this.rejectionReasonId!;
                result.rejectionReason = this.rejectionReason;
                result.otherReasonText = this.otherReasonText;
            }
        }
        this.dialogRef.close(result);
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onReasonChange(reasonId: number): void {
        const selectedReason = this.data.rejectionReasons.find(r => r.rejectionReasonId === reasonId);
        this.rejectionReasonId = reasonId;
        this.rejectionReason = selectedReason?.rejectionReason || '';
        if (!selectedReason?.rejectionReason?.includes('Other')) {
            this.otherReasonText = '';
        }
    }

    isOtherReason(): boolean {
        const reason = this.data.rejectionReasons.find(r => r.rejectionReasonId === this.rejectionReasonId);
        return !!reason?.rejectionReason?.includes('Other');
    }

    getFilteredReasons(): RejectionReason[] {
        const isModernization = this.data.application.hasAiRecommendedForModernization;
        if (isModernization == null) {
            return this.data.rejectionReasons;
        }
        return this.data.rejectionReasons.filter(r => {
            if (isModernization) {
                return r.rejectionCategory === 'Modernization';
            } else {
                return r.rejectionCategory === 'Decommission';
            }
        });
    }

    onUserSelected(user: UserSearchResult): void {
        if (user?.mail) {
            this.assignEmail = user.mail;
            this.emailControl.setValue(user.mail, { emitEvent: false });
            this.emailSelectedFromSearch = true;
            this.showSuggestions = false;
        }
    }

    onEmailBlur(): void {
        setTimeout(() => { this.showSuggestions = false; }, 150);
    }

    get confirmSummaryTitle(): string {
        switch (this.selectedAction) {
            case 'approve': return 'Approve Assessment';
            case 'override': return 'Override Assessment';
            case 'assign-technical': return 'Assign to Technical Team';
            case 'reject': return 'Reject Assessment';
            case 'assign-amore': {
                if (this.data.isAMORETeam) {
                    if (this.amoreAssignType === 'myself') return 'Assign to Myself';
                    if (this.amoreAssignType === 'architect') return 'Assign to Architect';
                    if (this.amoreAssignType === 'pod') return 'Assign to POD';
                }
                return 'Assign to AMORE Team';
            }
            case 'assign-pod': return 'Assign to POD';
            default: return '';
        }
    }

    get confirmSummaryDetail(): string {
        switch (this.selectedAction) {
            case 'approve':
                return `AI Recommendation: ${this.data.currentRecommendation}, please click submit to proceed with approval.`;
            case 'override':
                return `Final Decision: ${this.overrideAction}`;
            case 'assign-technical':
                return `Assign to Technical Owner (${this.assignEmail})`;
            case 'reject':
                return `Rejection reason: ${this.rejectionReason}${this.otherReasonText ? ' — ' + this.otherReasonText : ''}`;
            case 'assign-amore': {
                if (this.data.isAMORETeam) {
                    if (this.amoreAssignType === 'myself') {
                        return `Assign this application to yourself (${this.currentUserEmail}).`;
                    }
                    if (this.amoreAssignType === 'architect') {
                        return `Assign this application to Architect (${this.assignEmail}).`;
                    }
                    if (this.amoreAssignType === 'pod') {
                        const pod = this.pods.find(p => p.podId === this.selectedPodId);
                        return `Assign this application to POD: ${pod ? pod.name : '#' + this.selectedPodId}.`;
                    }
                }
                return 'Assign this application to the AMORE (ARM) team for implementation.';
            }
            case 'assign-pod': {
                const pod = this.pods.find(p => p.podId === this.selectedPodId);
                return `Assign this application to POD: ${pod ? pod.name : '#' + this.selectedPodId}.`;
            }
            default:
                return '';
        }
    }

    isValidEmail(email: string): boolean {
        return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
    }

    get stepLabel(): string {
        return String(this.step);
    }

    isStepCompleted(stepNum: number): boolean {
        return (this.step as number) > stepNum;
    }

    isStepActive(stepNum: number): boolean {
        return this.step === stepNum;
    }
}
